import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';

import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import { TmtLoggerService } from 'tmt-logger';
import { MessageService } from 'primeng/api';

import { loginEndForWindowsApps } from 'src/app/components/login/loginJSFunctions.js';
import { UserData } from 'src/app/models/user-data';
import { AppConfigService } from 'src/app/services/app-config.service';
import { AuthService } from 'src/app/services/auth.service';
import { SessionService } from 'src/app/services/session.service';
import { UtilsService } from 'src/app/services/utils.service';

/**
 * Handles login logic, validating and updating user session data.
 */
@Injectable()
export class LoginService {
	/**
	 * Constructor.
	 * @param appConfigService To get configuration settings.
	 * @param loggerService To log status.
	 * @param authService To access and edit configuration.
	 * @param sessionService To handle user session.
	 * @param router To handle navigation.
	 * @param utilityService To navigate to external site.
	 * @param document
	 * @param messageService
	 */
	constructor(
		private appConfigService: AppConfigService,
		private loggerService: TmtLoggerService,
		private authService: AuthService,
		private sessionService: SessionService,
		private router: Router,
		private utilityService: UtilsService,
		@Inject(DOCUMENT) private document: Document,
		private messageService: MessageService,
	) {}

	/**
	 * Returns an observable which performs necessary operations to validate and/or create user session.
	 * @param origin$ Used to redirect back to origin when third party is using sign-in.
	 * @param colaCode$ Used to create a session when code is provided by cola.
	 * @returns Observable representing the user session handling.
	 */
	public handleUserSession$(origin$: Observable<string>, colaCode$: Observable<string>) {
		return colaCode$.pipe(
			switchMap(colaCode => {
				// First, if we have a cola code, we should use it to open a session.
				// Otherwise, check current state to validate existing session.
				if (colaCode) {
					// If we have a cola code.
					return combineLatest({
						origin: origin$,
						userData: this.sessionService.openSession$(colaCode, this.appConfigService.getConfig().auth_provider).pipe(
							take(1),
							tap(data => {
								// If data from cola does not contain a session ID, redirect to landing page.
								// Do not retry autentication, as that could cause a loop if errors persist.
								if (!data?.sessionId) {
									this.router.navigateByUrl('');
								}
								this.authService.setUserData(data!);
							}),
						),
					}).pipe(
						filter(data => !!data.userData),
						// Create one time token, then return original data with token appended.
						switchMap(data =>
							this.createOneTimeToken$().pipe(
								map(token => {
									return { ...data, token: token };
								}),
							),
						),
						tap(data => {
							if (data.origin) {
								this.returnToOrigin(data.origin, data.userData!, data.token);
							} else {
								this.router.navigateByUrl('');
							}
						}),
					);
				} else {
					// If no cola code, validate session if exists.
					return combineLatest({
						origin: origin$,
						userData: this.authService.userData$.pipe(take(1)),
						isValidSession: this.authService.isLoggedIn$.pipe(
							take(1),
							switchMap(isLoggedIn => {
								if (isLoggedIn) {
									return this.sessionService.validateSession$().pipe(take(1));
								} else {
									return of(false);
								}
							}),
						),
					}).pipe(
						// Create one time token if valid session, then return original data with token appended.
						switchMap(data =>
							data.isValidSession && data.origin
								? this.createOneTimeToken$().pipe(
										map(token => {
											return { ...data, token: token };
										}),
									)
								: of({ ...data, token: undefined }),
						),
						map(data => {
							if (data.isValidSession && data.origin) {
								this.returnToOrigin(data.origin, data.userData, data.token);
							} else if (data.isValidSession) {
								this.router.navigateByUrl('');
							} else {
								this.authenticateOnCola();
							}
						}),
					);
				}
			}),
		);
	}

	/**
	 * Tries to create a one time token.
	 * @returns One time token if successful otherwise "noAuth".
	 */
	private createOneTimeToken$(): Observable<string> {
		return this.sessionService.createOneTimeToken$().pipe(
			map(result => (result ? result : 'noAuth')),
			catchError(() => of('noAuth')),
		);
	}

	/**
	 * Navigates to a provided origin url.
	 * @param originUrl Url to navigate to.
	 * @param userData User data which should be provided as query params to origin.
	 * @param token
	 */
	private returnToOrigin(originUrl: string, userData: UserData, token: string | undefined) {
		// Ensure that origin is cleared for future sign-ins.
		localStorage.removeItem('origin');
		localStorage.removeItem('originTimestamp');

		this.loggerService.logDebug('Return to origin: ' + originUrl + ' ' + userData.sessionId);
		if (originUrl !== null) {
			// If the redirect url is "Windows", we call the function which calls function in windows app
			if (originUrl.toLowerCase() === 'windows') {
				loginEndForWindowsApps(userData.sessionId, userData.userName, userData.company);
			}
			this.loggerService.logDebug('redirect');

			const oneTimeTokenString = `fenixOneTimeToken=${token}`;
			const legacyString = `sessionId=${userData.sessionId}&username=${userData.userName}&company=${userData.company}`;

			// Some sites are moving to the new authentication with one time tokens.
			const sitesUsingOneTimeToken = [
				'ecucompare',
				'ddb',
				'localhost',
				'reports.testtools',
				'results.testtools',
				'configurator.testtools',
				'nexus.testtools',
				'executor.testtools',
				'reports.tms.test',
				'results.tms.test',
				'drivingrestrictions.testtools',
				'restrictions.testtools',
				'deliverydatabase',
				'sesoco3895:86',
				'analyzer.testtools',
				'webanalytics.testtools',
				'bookings.testtools',
				'chimera.testtools',
			];

			// Some sites still use the old legacy auth string, otherwise remove them.
			const sitesNotUsingLegacy = [
				'ecucompare',
				'ddb',
				'reports.testools',
				'results.testtools',
				'configurator.testtools',
				'nexus.testtools',
				'executor.testtools',
				'reports.tms.test',
				'results.tms.test',
				'drivingrestrictions.testtools',
				'restrictions.testtools',
				'sesoco3895:86',
				'chimera.testtools',
			];

			let queryParamString: string;

			// Check which authentication types origin site is using.
			const isUsingOneTimeToken = sitesUsingOneTimeToken.findIndex(item => originUrl.includes(item)) !== -1;
			const isUsingLegacy = sitesNotUsingLegacy.findIndex(item => originUrl.includes(item)) === -1;

			// An application may use both legacy and one time token temporarily, to allow for seamless migration.
			// This should be during as short a time as possible.
			// When one time token has been implemented for your application, remove legacy auth ASAP.
			if (isUsingLegacy && isUsingOneTimeToken) {
				queryParamString = `${legacyString}&${oneTimeTokenString}`;
			} else if (isUsingOneTimeToken) {
				queryParamString = oneTimeTokenString;
			} else if (isUsingLegacy) {
				queryParamString = legacyString;
			} else {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not authenticate origin URL.' });
				return;
			}

			if (originUrl.includes('?')) {
				originUrl += `&${queryParamString}`;
			} else {
				originUrl += `?${queryParamString}`;
			}
			this.utilityService.navigate(originUrl);
		}
	}

	/**
	 * Redirects to cola for authentication.
	 */
	private authenticateOnCola() {
		this.utilityService.navigate(this.appConfigService.getLoginUrl());
	}
}
